import { Component } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UserWithRelationships } from '@app/models/user.interface';
import { UserHelperService } from '@app/services/helpers/user-helper.service';
import { Observable } from 'rxjs';
import { HeadMetaComponent } from "@app/components/head-meta/head-meta.component";
import { LoadingErrorBlockComponent } from "@app/components/loading-error-block/loading-error-block.component";
import { LetDirective } from '@ngrx/component';
import { CreatedUpdatedDisplayComponent } from "@app/components/created-updated-display/created-updated-display.component";
import { DetailDisplayComponent } from "@app/components/detail-display/detail-display.component";
import { faCheck, faPenToSquare, faXmark, faArrowLeft, faBan, faCircleCheck, faExclamationTriangle, faKey, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AuthService } from '@app/services/auth.service';
import { HelpButtonComponent } from "@app/components/help-button/help-button.component";
import { ToastService } from '@app/services/toast.service';
import { AppHttpError } from '@app/interfaces/pg-models.interface';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-user-detail',
    templateUrl: './detail.page.html',
    styleUrl: './detail.page.scss',
    imports: [
        HeadMetaComponent,
        LoadingErrorBlockComponent,
        LetDirective,
        CreatedUpdatedDisplayComponent,
        DetailDisplayComponent,
        FontAwesomeModule,
        RouterModule,
        HelpButtonComponent,
        CommonModule
    ]
})
export class UserDetailPage {
  title = "User Detail";
  id: string;
  user$: Observable<UserWithRelationships>;

  faCheck = faCheck;
  faXmark = faXmark;
  faPenToSquare = faPenToSquare;
  faArrowLeft = faArrowLeft;
  faBan = faBan;
  faCircleCheck = faCircleCheck;
  faExclamationTriangle = faExclamationTriangle;
  faKey = faKey;
  faCopy = faCopy;

  generatedPassword: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private userHelper: UserHelperService,
    public auth: AuthService,
    private toast: ToastService,
  ) {
    this.id = this.route.snapshot.params['id'];
    this.user$ = this.userHelper.getById(this.id);
  }

  public disableUser(user: UserWithRelationships) {
    if (confirm(`Disable ${user.display_name}? They will not be able to log in.`)) {
      this.userHelper.disableUser(this.id).subscribe({
        next: () => {
          this.user$ = this.userHelper.getById(this.id);
          this.toast.message({
            message: 'User disabled successfully',
            type: 'success'
          });
        },
        error: (err: AppHttpError) => {
          this.toast.message({
            message: err.error.message,
            type: 'error'
          });
        }
      });
    }
  }

  public setPassword(user: UserWithRelationships) {
    if (confirm(`Set a new password for ${user.display_name}? This will override their current password.`)) {
      this.userHelper.setUserPassword(this.id).subscribe({
        next: (password: string) => {
          this.generatedPassword = password;
          const modal = document.getElementById('password_modal') as HTMLDialogElement;
          modal?.showModal();
        },
        error: (err: AppHttpError) => {
          this.toast.message({
            message: err.error.message,
            type: 'error'
          });
        }
      });
    }
  }

  public copyPassword() {
    if (this.generatedPassword) {
      navigator.clipboard.writeText(this.generatedPassword).then(() => {
        this.toast.message({
          message: 'Password copied to clipboard',
          type: 'success'
        });
      });
    }
  }

  public enableUser(user: UserWithRelationships) {
    if (confirm(`Enable ${user.display_name}?`)) {
      this.userHelper.enableUser(this.id).subscribe({
        next: () => {
          this.user$ = this.userHelper.getById(this.id);
          this.toast.message({
            message: 'User enabled successfully',
            type: 'success'
          });
        },
        error: (err: AppHttpError) => {
          this.toast.message({
            message: err.error.message,
            type: 'error'
          });
        }
      });
    }
  }
}
